'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UpsertApplicationInternalAccessBodySchema } from '@terra/shared-contracts-applications';
import type { z } from 'zod';
import { terraProxyApiClient } from '../../clients/testerClient';
import { APPLICATION_BY_ID_QUERY_KEY } from './useApplicationById';

export const APPLICATION_INTERNAL_ACCESS_KEY = 'application-internal-access';
export const TARGET_TWINGATE_NETWORKS_KEY = 'target-twingate-networks';
export const TARGET_CONNECTORS_KEY = 'target-connectors';

type UpsertApplicationInternalAccessBody = z.infer<
  typeof UpsertApplicationInternalAccessBodySchema
>;

const EMPTY_LIST: never[] = [];

export function useApplicationInternalAccessSettings({
  applicationId,
  enabled,
}: {
  applicationId: string;
  enabled: boolean;
}) {
  const queryClient = useQueryClient();
  const internalAccessQuery = useQuery({
    queryKey: [APPLICATION_INTERNAL_ACCESS_KEY, applicationId],
    queryFn: async () => {
      const { internalAccess } =
        await terraProxyApiClient.getApplicationInternalAccess(applicationId);

      return internalAccess;
    },
    enabled: enabled && Boolean(applicationId),
  });
  const invalidateInternalAccess = () =>
    queryClient.invalidateQueries({
      queryKey: [APPLICATION_INTERNAL_ACCESS_KEY, applicationId],
    });
  const upsertBinding = useMutation({
    mutationFn: async (body: UpsertApplicationInternalAccessBody) => {
      const { internalAccess } =
        await terraProxyApiClient.upsertApplicationInternalAccess({
          applicationId,
          body,
        });

      return internalAccess;
    },
    onSuccess: async () => {
      await Promise.all([
        invalidateInternalAccess(),
        queryClient.invalidateQueries({
          queryKey: [APPLICATION_BY_ID_QUERY_KEY, applicationId],
        }),
      ]);
    },
  });
  const testConnection = useMutation({
    mutationFn: () =>
      terraProxyApiClient.testApplicationInternalAccess(applicationId),
    onSuccess: async () => {
      await invalidateInternalAccess();
    },
  });

  return {
    internalAccess: internalAccessQuery.data ?? null,
    isLoading: internalAccessQuery.isLoading,
    upsertBinding,
    testConnection,
  };
}

export function useTargetTwingateNetworks({ enabled }: { enabled: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: [TARGET_TWINGATE_NETWORKS_KEY],
    queryFn: async () => {
      const { result } = await terraProxyApiClient.listTargetTwingateNetworks();

      return result;
    },
    enabled,
  });

  return { networks: data ?? EMPTY_LIST, isLoading: enabled && isLoading };
}

export function useTargetConnectors({ enabled }: { enabled: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: [TARGET_CONNECTORS_KEY],
    queryFn: async () => {
      const { result } = await terraProxyApiClient.listTargetConnectors();

      return result;
    },
    enabled,
  });

  return { connectors: data ?? EMPTY_LIST, isLoading: enabled && isLoading };
}
